import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonModal from "../../../components/modals/commonModel";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import CommonForm from "../../../components/form/form";
import {
  createLeaveType,
  deleteLeaveType,
  getLeaveTypeList,
  updateLeaveType,
} from "../../../../services/workspace/leaveType.service";
import {
  createDataTransformer,
  presetTransformers,
} from "../../../../utils/dataTransformers";
import {
  createActionButtons,
  commonConditions,
} from "../../../../utils/actionButtonHelpers";
import { _GenderData } from "../../../../utils/common";
import CustomTab, { TabContainer } from "../../../components/tabs/customTabs";
import PoliciesRulesSection from "./components/PoliciesRulesSection"; 
import ApproverConfigSection from "./components/ApproverConfigSection";
import BalanceManagementSection from "./components/BalanceManagementSection";
import NotificationsSection from "./components/NotificationsSection";
import LeaveAllocationSection from "./components/LeaveAllocationSection";
import AuditLogsSection from "./components/AuditLogsSection";
import {
  getLeaveApprovalList,
  getWorkspaceDetail,
  updateWorkspace,
} from "../../../../services/workspace/workspace.service";
import { allocateBulkLeave, allocateLeave, allocateAnnualLeave, updateApproval } from "../../../../services/workspace/leave.service";
import { useSelector } from "react-redux";
import { useFilterOptions } from "../../../../hooks";
import BottomDrawer from "../../common/drawer/bottomDrawer";

const actions_type = {
  DELETE: "DELETE",
  ADD_EDIT: "ADD_EDIT",
};

const items = [{ label: "Manage Leave Type", path: "" }];

const ManageLeaveSetup = () => {
  const [isEditValue, setIsEditValue] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [policiesInitialData, setPoliciesInitialData] = useState(null);
  const [workspaceDetail, setWorkspaceDetail] = useState(null);
  const [leaveApprovalsInitialData, setLeaveApprovalsInitialData] = useState(null);
 

  const { 
    designations,
  
  } = useFilterOptions([  "designations"]);

  
  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
  } = useCommonFetch(getLeaveTypeList, {
    dataPath: "leaveType.data",
    totalPath: "leaveType.meta.total",
    errorMessage: "Failed to fetch leave data!",
    debounceDelay: 500,
    sortingOptions: [
      { label: "Name", field: "name" },
      { label: "Abbreviation", field: "abrv" },
    ],
    sortingModule: "leave",
    orderParamNames: {
      orderByKey: "order_by",
      orderTypeKey: "order_type",
      includeDisabledKey: "includeDisabled",
    },
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,

    dataTransformer: createDataTransformer(
      presetTransformers.leaveSetup(),
      () =>
        createActionButtons({
          delete: {
            handler: (item) => {
              handleDelete(item.id.data, deleteLeaveType, {
                successMessage: "Leave-type deleted successfully!",
                errorMessage: "Failed to delete leave-type!",
              });
            },
            show: commonConditions.canDelete,
          },
          edit: {
            handler: (item) => {
              openModal(item, actions_type.ADD_EDIT);
              
              // Extract accrual_type - it comes from transformer as { data: "monthly" }
              const accrualTypeValue = item.accrual_type?.data;
              
              
              const InitialData = {
                id: item.id.data,
                code: item.code?.data || "",
                abrv: item.abrv.data,
                allotted: item.allotted.data,
                name: item?.name.data,
                // Set accrual_type - ensure it matches one of the select options: null, "monthly", "yearly", "quarterly"
                accrual_type: accrualTypeValue || null,
                accrual_rate: item.accrual_rate?.data || 0,
                // Map carry_forward back to carry_forward for the form
                carry_forward: item.carry_forward?.data || 0,
                applicable_to:
                  item.applicable_to?.data ||
                  item.gender_specific?.data ||
                  "all",
                // Map encashable to is_encashable for the form
                is_encashable: item.encashable?.data || false,
                requires_doc_above_days:
                  item.requires_doc_above_days?.data || 0,
                description: item.description.data,
                // Map backend key (gender_specific) to form key (applicable_to)
                applicable_to:
                  item.applicable_to?.data ||
                  item.gender_specific?.data ||
                  "all",
              };
              setIsEditValue(InitialData);
            },
            show: commonConditions.canEdit,
          },
        })
    ),
  });

  // Comprehensive form fields for Leave Types
  const comprehensiveFields = [
    {
      name: "code",
      label: "Leave Code",
      type: "text",
      placeholder: "Enter leave code (e.g., CL, SL, EL)",
      required: true,
      colSpan: 6,
      info: "Unique code for the leave type. Example: CL (Casual Leave), SL (Sick Leave), EL (Earned Leave). This code is used in reports and leave applications.",
    },
    {
      name: "name",
      label: "Leave Name",
      type: "text",
      placeholder: "Enter leave name",
      required: true,
      colSpan: 6,
      info: "Full name of the leave type. Example: 'Casual Leave', 'Sick Leave', 'Earned Leave'. This appears in employee leave applications.",
    },
    {
      name: "abrv",
      label: "Abbreviation",
      type: "text",
      placeholder: "Enter abbreviation name",
      colSpan: 6,
      info: "Short form of the leave name. Example: CL, SL, EL. Used in quick references and displays.",
    },
    {
      name: "accrual_type",
      label: "Accrual Type",
      type: "select",
      options: [
        { label: "None", value: null },
        { label: "Monthly", value: "monthly" },
        { label: "Yearly", value: "yearly" },
        { label: "Quarterly", value: "quarterly" },
      ],
      initialValue: null,
      placeholder: "Select accrual type",
      colSpan: 6,
      info: "How leave balance increases over time. Monthly: 1.5 days/month = 18 days/year. Yearly: All at once on Jan 1st. Quarterly: Every 3 months. None: Manual allocation only.",
    },
    {
      name: "accrual_rate",
      label: "Accrual Rate",
      type: "number",
      initialValue: 0,
      placeholder: "Enter accrual rate (e.g., 1.5)",
      colSpan: 6,
      info: "Number of days added per accrual period. Example: 1.5 days/month means employees get 1.5 days every month. If accrual type is Monthly and rate is 1.5, employee gets 18 days per year.",
    },
    {
      name: "carry_forward",
      label: "Max Carry-Forward (Days)",
      type: "number",
      initialValue: 0,
      placeholder: "Enter max carry-forward days",
      colSpan: 6,
      info: "Maximum unused leave days that can be carried to next year. Example: If set to 5, employee can carry max 5 unused days. Excess days will be forfeited or encashed (if encashable).",
    },
    {
      name: "allotted",
      label: "Allotted Leave",
      type: "number",
      initialValue: 0,
      placeholder: "Enter allotted leave days",
      required: true,
      colSpan: 6,
      info: "Initial/annual leave balance allocated to employees. Example: 12 days/year for CL. This is the starting balance for new employees or annual reset amount.",
    },
    {
      name: "requires_doc_above_days",
      label: "Requires Document Above (Days)",
      type: "number",
      initialValue: 0,
      placeholder: "Enter days threshold for document requirement",
      colSpan: 6,
      info: "If leave exceeds this number of days, medical certificate/document is mandatory. Example: If set to 3, leaves of 4+ days require medical certificate. Leave applications above this threshold will be rejected without documents.",
    },
    {
      name: "applicable_to",
      label: "Gender Specific",
      type: "select",
      options: [
        { label: "All Genders", value: "all" },
        { label: "Male Only", value: "male" },
        { label: "Female Only", value: "female" },
        { label: "Other Only", value: "other" },
      ],
      placeholder: "Select gender restriction (optional)",
      colSpan: 6,
      info: "Restrict this leave type to specific gender. Example: Maternity Leave = Female Only, Paternity Leave = Male Only. If 'All Genders' is selected, leave is available to everyone.",
    },
    {
      name: "is_encashable",
      label: "Encashable",
      type: "checkbox",
      colSpan: 6,
      info: "If enabled, unused leave can be converted to cash. Example: Employee has 10 unused CL days, can encash them at year-end. Impact: Employees can request encashment, HR can process bulk encashment.",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      placeholder: "Write description here...",
      maxLength: 300,
      colSpan: 12,
      info: "Detailed description of the leave type, its purpose, and usage guidelines. This helps employees understand when to use this leave type.",
    },
  ];

  const TableHeaderData = [
    {
      label: "Code",
      key: "code",
    },
    {
      label: "Leave Name",
      key: "name",
    },
    {
      label: "Abbreviation",
      key: "abrv",
    },
    {
      label: "Accrual Type",
      key: "accrual_type",
    },
    {
      label: "Accrual Rate",
      key: "accrual_rate",
    },
    {
      label: "Allotted",
      key: "allotted",
    },
    {
      label: "Max Carry-Forward",
      key: "carry_forward",
    },
    {
      label: "Gender Specific",
      key: "applicable_to",
    },
    {
      label: "Encashable",
      key: "is_encashable",
    },
    {
      label: "Requires Doc Above",
      key: "requires_doc_above_days",
    },
    {
      label: "Created At",
      key: "created_at",
    },
  ];

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // Transform form values to match API expectations
    const transformedValues = {
      ...values,
      // Ensure accrual_type has a default value
      accrual_type: values.accrual_type || null,
      // Map field names correctly
      carry_forward: values.carry_forward || 0,
      // Ensure numeric fields are sent as numbers, not empty strings
      accrual_rate: values.accrual_rate || 0,
      requires_doc_above_days: values.requires_doc_above_days || 0,
      // Convert boolean to appropriate format
      is_encashable: values.is_encashable === true ? 1 : 0,
      // Keep both keys for backend compatibility
      applicable_to: values.applicable_to || "all",
      gender_specific: values.applicable_to || "all",
    };

    // Remove undefined values
    Object.keys(transformedValues).forEach(key => {
      if (transformedValues[key] === undefined || transformedValues[key] === "") {
        if (key === "accrual_rate" || key === "carry_forward" || key === "requires_doc_above_days") {
          transformedValues[key] = 0;
        }
      }
    });

  
    let result;
    // Edit
    if (isEditValue) {
      result = await handleUpdate(transformedValues, isEditValue.id, updateLeaveType, {
        successMessage: "Leave-type updated successfully!",
        errorMessage: "Failed to update Leave-type!",
      });
    } else {
      // Create new
      result = await handleCreate(transformedValues, createLeaveType, {
        successMessage: "Leave-type created successfully!",
        errorMessage: "Failed to create Leave-type!",
      });
    }

    setSubmitting(false);
    
    // Only reset form if operation was successful
    if (result?.success) {
      setTimeout(() => {
        resetForm();
        setIsEditValue(null);
      }, 1000);
    }
    // If error, form fields remain populated so user can fix and resubmit
  };

  // Handle form cancellation
  const handleCancel = () => {

    setIsEditValue(null);
    closeModal();
  };

  // Policies/Rules form fields
  const policiesFields = [
    {
      name: "probation_period_days",
      label: "Probation Period (Days)",
      type: "number",
      placeholder: "Enter probation period in days",
      colSpan: 4,
      info: "Employees on probation cannot apply for leave during this period. Example: If set to 90 days, new employees cannot take leave for first 3 months. Impact: Leave applications from probationary employees will be automatically rejected.",
    },
    {
      name: "backdate_window_days",
      label: "Backdate Window (Days)",
      type: "number",
      placeholder: "Enter backdate window in days",
      colSpan: 4,
      info: "Maximum days in the past for which leave can be applied. Example: If set to 7, employees can apply for leave up to 7 days back. Impact: Prevents very old leave applications and ensures timely reporting.",
    },
    {
      name: "max_consecutive_days",
      label: "Max Consecutive Days",
      type: "number",
      placeholder: "Enter maximum consecutive leave days",
      colSpan: 4,
      info: "Maximum continuous leave days allowed in a single application. Example: If set to 15, employees cannot apply for more than 15 consecutive days. Impact: Long leave applications will be rejected, encouraging proper planning.",
    },
    {
      name: "is_count_weekends",
      label: "Count Weekends in Leave",
      type: "checkbox",
      colSpan: 6,
      info: "If enabled, Saturdays and Sundays are counted in leave days. If disabled, only working days are counted. Example: 5-day leave from Mon-Fri = 5 days if disabled, 7 days if enabled (includes Sat-Sun). Impact: Affects leave balance deduction calculation.",
    },
  ];

  // Org-level Settings form fields
  const orgSettingsFields = [
    {
      name: "working_days",
      label: "Working Days",
      type: "select",
      options: [
        { label: "Monday", value: "monday" },
        { label: "Tuesday", value: "tuesday" },
        { label: "Wednesday", value: "wednesday" },
        { label: "Thursday", value: "thursday" },
        { label: "Friday", value: "friday" },
        { label: "Saturday", value: "saturday" },
        { label: "Sunday", value: "sunday" },
      ],
      multiple: true,
      placeholder: "Select working days",
      colSpan: 12,
      info: "Select which days of the week are working days. Example: Mon-Fri for 5-day week, Mon-Sat for 6-day week. Impact: Leave calculation will only count selected days. If weekend counting is disabled, only these days are deducted from leave balance.",
    },
    {
      name: "timezone",
      label: "Timezone",
      type: "select",
      options: [
        { label: "IST (India Standard Time)", value: "Asia/Kolkata" },
        { label: "UTC", value: "UTC" },
        { label: "EST (Eastern Standard Time)", value: "America/New_York" },
        { label: "PST (Pacific Standard Time)", value: "America/Los_Angeles" },
      ],
      placeholder: "Select timezone",
      colSpan: 12,
      info: "Organization's timezone for leave date calculations and reporting. Example: IST for India-based companies. Impact: All leave dates, deadlines, and reports will use this timezone. Ensures consistency across global teams.",
    },
  ];

  // Approver Config form fields
  const approverConfigFields = [
    {
      name: "designation",
      label: "Designation",
      type: "dynamicSearchSelect", 
      colSpan: 12,
      required: true,
      options: designations.map((d) => ({
        label: d.name,
        value: d.uuid,
      })), 
    },

    {
      name: "allow_delegation",
      label: "Allow Delegation",
      type: "checkbox",
      colSpan: 12,
      info: "If enabled, approvers can delegate their approval authority...",
    },

    {
      name: "default_approver_fallback",
      label: "Default Approver Fallback",
      type: "text",
      placeholder: "Enter fallback approver",
      colSpan: 12,
      info: "Fallback approver when primary approver is unavailable.",
    },
    // {
    //   name: "default_approval_flow",
    //   label: "Default Approval Flow",
    //   type: "select",
    //   options: [
    //     { label: "Manager Only", value: "manager_only" },
    //     { label: "Manager + HR", value: "manager_hr" },
    //     { label: "Manager + HR + Admin", value: "manager_hr_admin" },
    //   ],
    //   placeholder: "Select default approval flow",
    //   colSpan: 12,
    //   info: "Default approval chain for leave applications. Manager Only: Only direct manager approves. Manager + HR: Manager approves first, then HR. Manager + HR + Admin: Three-level approval. Impact: All new leave applications follow this flow unless overridden per employee/leave type.",
    // },
    // {
    //   name: "allow_delegation",
    //   label: "Allow Delegation",
    //   type: "checkbox",
    //   colSpan: 12,
    //   info: "If enabled, approvers can delegate their approval authority to other users when they are unavailable. Example: Manager on leave can delegate to deputy manager. Impact: Delegated approvers receive notifications and can approve on behalf of original approver.",
    // },
    // {
    //   name: "default_approver_fallback",
    //   label: "Default Approver Fallback",
    //   type: "text",
    //   placeholder: "Enter default approver fallback (e.g., HR Manager, Admin)",
    //   colSpan: 12,
    //   info: "Fallback approver when primary approver is unavailable or not assigned. Example: 'HR Manager' or 'Admin'. Impact: If employee's manager is not set or unavailable, leave goes to this fallback approver automatically.",
    // },
  ];

  // Balance Management form fields
  const balanceManagementFields = [
    {
      name: "adjustment_type",
      label: "Adjustment Type",
      type: "select",
      options: [
        { label: "Add Days", value: "add" },
        { label: "Subtract Days", value: "subtract" },
        { label: "Set Balance", value: "set" },
      ],
      placeholder: "Select adjustment type",
      colSpan: 6,
      info: "Type of balance adjustment. Add: Increase balance (e.g., +5 days). Subtract: Decrease balance (e.g., -3 days). Set: Replace balance with exact value (e.g., set to 10 days). Impact: Directly modifies employee leave balance. Use for corrections, bonuses, or penalties.",
    },
    {
      name: "adjustment_days",
      label: "Days",
      type: "number",
      placeholder: "Enter number of days",
      colSpan: 6,
      info: "Number of days to add, subtract, or set. Example: If type is 'Add' and days is 5, employee balance increases by 5. If type is 'Set' and days is 10, balance becomes exactly 10. Impact: Immediate balance update, logged in audit trail.",
    },
  ];

  // Notifications Settings form fields
  const notificationsFields = [
    {
      name: "enable_email",
      label: "Enable Email Notifications",
      type: "checkbox",
      colSpan: 12,
      info: "Send email notifications for leave applications, approvals, rejections, and reminders. Impact: Employees and approvers receive email alerts. Example: Employee applies for leave → Manager receives email notification → Manager approves → Employee receives confirmation email.",
    },
    {
      name: "enable_sms",
      label: "Enable SMS Notifications",
      type: "checkbox",
      colSpan: 12,
      info: "Send SMS notifications for urgent leave updates. Impact: Real-time SMS alerts for critical actions. Example: Leave application submitted → Manager receives SMS → Approval deadline reminder via SMS. Note: Requires SMS gateway configuration.",
    },
    {
      name: "enable_slack",
      label: "Enable Slack Notifications",
      type: "checkbox",
      colSpan: 12,
      info: "Send Slack notifications to configured channels for leave updates. Impact: Team channels receive leave notifications. Example: Leave application → #hr-notifications channel gets message → Manager can approve directly from Slack. Note: Requires Slack integration setup.",
    },
  ];

  // Handle form submissions for different sections
  const handlePoliciesSubmit = async (values) => {
    const payload = { ...values, ws_type: 2 };

    // Convert boolean to 0/1 for API
    if (payload.is_count_weekends !== undefined) {
      payload.is_count_weekends = payload.is_count_weekends ? 1 : 0;
    }

    // Conditionally include blackout_dates in payload only if they exist
    // blackout_dates can come from form submit or from blackoutDates state
    if (values.blackout_dates && values.blackout_dates.length > 0) {
      payload.blackout_dates = values.blackout_dates;
    } else if (blackoutDates && blackoutDates.length > 0) {
      payload.blackout_dates = blackoutDates;
    }

    await handleUpdate(payload, workspaceDetail?.id, updateWorkspace, {
      successMessage: "Policies updated successfully!",
      errorMessage: "Failed to update Policies!",
      onSuccess: () => {
        fetchPoliciesDetail(); // refresh after update
      },
    });
  };

  const fetchPoliciesDetail = () => {
    getWorkspaceDetail({ additional_info: true })
      .then((response) => {
        const ws = response.workspace;

        const InitialData = {
          id: ws.id, // keep ID for update

          // Basic Information
          logo: ws.image || "",
          alternate_logo: ws.alternate_logo || "",
          workspace_name: ws.name || "",
          tagline: ws.tagline || "",
          ws_start: ws.ws_start || "",
          ws_end: ws.ws_end || "",
          ws_minutes: ws.ws_minutes || "",
          ws_relax_time: ws.ws_relax_time || "",

          // Contact Information
          country_code2: ws.country_code2 || "",
          phone: ws.phone || "",
          phone2: ws.phone2 || "",
          email: ws.email || "",
          website: ws.website || "",

          // Notification Information
          is_email_notification: ws.is_email_notification === 1,
          is_sms_notification: ws.is_sms_notification === 1,
          is_push_notification: ws.is_push_notification === 1,

          // Social Information
          instagram: ws.instagram || "",
          linkedin: ws.linkedin || "",
          twitter: ws.twitter || "",
          facebook: ws.facebook || "",

          // Location Information
          address: ws.address || "",
          latitude: ws.latitude || "",
          longitude: ws.longitude || "",
        };
        setWorkspaceDetail(InitialData);

        // Set Policies data according to policiesFields keys
        const policiesData = {
          probation_period_days: ws.probation_period_days || "",
          backdate_window_days: ws.backdate_window_days || "",
          max_consecutive_days: ws.max_consecutive_days || "",
          // Convert 0/1 to boolean for checkbox form field
          is_count_weekends:
            ws.is_count_weekends === 1 || ws.is_count_weekends === true,
        };
        setPoliciesInitialData(policiesData);

        // Set blackout dates if they exist in the response
        if (ws.blackout_dates && Array.isArray(ws.blackout_dates)) {
          setBlackoutDates(ws.blackout_dates);
        } else if (ws.blackout_dates && typeof ws.blackout_dates === "string") {
          // If blackout_dates is a string (JSON), parse it
          try {
            const parsed = JSON.parse(ws.blackout_dates);
            if (Array.isArray(parsed)) {
              setBlackoutDates(parsed);
            }
          } catch (e) {
            console.error("Error parsing blackout_dates:", e);
          }
        }
      })
      .catch((error) => {
        toast.error("Failed to fetch workspace data!");
      });
  };

  const handleOrgSettingsSubmit = async (values) => {
    // Add API call here
  };

  const handleApproverConfigSubmit = async (values) => {
    // Prepare payload for API
    // The API expects approval_flow as the key (not designations)
    const payload = {
      designations: values.designations || [], // Array of designation IDs
      // allow_delegation: values.allow_delegation ? 1 : 0,
      // default_approver_fallback: values.default_approver_fallback || "",
    };

    // Note: updateApproval API doesn't use ID in URL, but handleUpdate expects it
    // The ID parameter might be ignored by the API
    await handleUpdate(payload, workspaceDetail?.id, updateApproval, {
      successMessage: "Approver config updated successfully!",
      errorMessage: "Failed to update Approver config!",
      onSuccess: () => {
        fetchLeaveApprovals(); // refresh after update
      },
    });
  };

  const fetchLeaveApprovals = () => {
    getLeaveApprovalList()
      .then((response) => {
        const ws = response.workspace;

        // Parse leaveApprovals - it's an array of designation objects
        // Extract id (not uuid) from each designation object
        let approvalFlowDesignationIds = [];
        if (ws.leaveApprovals && Array.isArray(ws.leaveApprovals)) {
          approvalFlowDesignationIds = ws.leaveApprovals.map((designation) => {
            // Always use id (not uuid)
            return designation.id;
          });
        }

        const InitialData = {
          id: ws.id, // keep ID for update 
          designations: approvalFlowDesignationIds, // Array of designation IDs (uuid or id)
          allow_delegation: ws.allow_delegation === 1 || ws.allow_delegation === true,
          default_approver_fallback: ws.default_approver_fallback || "",
        };
        setLeaveApprovalsInitialData(InitialData);
      })
      .catch((error) => {
        toast.error("Failed to fetch leave approvals data!");
        console.error("Error fetching leave approvals:", error);
      });
  };

  const handleBalanceAdjust = async (values) => {
    // Add API call here
  };

  const handleNotificationsSubmit = async (values) => {
    // Add API call here
  };

  // Leave Allocation handlers
  const handleIndividualAllocation = async (values) => {
    const payload = {
      user_id: values.employee_id,
      leave_type_id: values.leave_type_id,
      allotted: values.days,
      description: values.reason,
      start_date: values.allocation_date,
      end_date: values.valid_until,
    };

    await handleCreate(payload, allocateLeave, {
      successMessage: "Leave allocated successfully!",
      errorMessage: "Failed to allocate leave!",
    });
  };

  const handleBulkAllocation = async (values) => {

   const payload = {
      department_id: values.department_id,
      leave_type_id: values.leave_type_id,
      allotted: values.days,
      applicable_to: "all",
      description: values.reason,
      start_date: values.allocation_date,
      end_date: values.valid_until,
      designations: values.designation_id
  }

  await handleCreate(payload, allocateBulkLeave, {
    successMessage: "Leave allocated successfully to bulk employees!",
    errorMessage: "Failed to allocate leave to bulk employees!",
  });

  };

  const handleAnnualAllocation = async (payload) => {
    try {
    
      const response = await allocateAnnualLeave(payload);
      toast.success(response?.message || "Annual leave allocation successful!");
      // Optionally refresh any data or show success details
    } catch (error) {
      console.error("Annual allocation error:", error);
      toast.error(error?.response?.data?.message || "Failed to allocate annual leave!");
    }
  };

  const handleCSVImport = (files) => {
    // Add CSV import logic here
  };

  const handleEncashment = () => {
    // Add encashment logic here
  };

  const handleYearEndCarryForward = () => {
    // Add year-end carry-forward logic here
  };

  // Audit Logs table headers
  const auditLogsHeaders = [
    { label: "Date & Time", key: "created_at" },
    { label: "User", key: "user" },
    { label: "Action", key: "action" },
    { label: "Section", key: "section" },
    { label: "Changes", key: "changes" },
  ];

  // Tabs configuration
  const tabs = [
    {
      id: "leave-types",
      label: "Leave Types",
      content: (
        <div className="flex flex-col gap-4 p-4">
          {/* Section Description */}
          <div className="bg-information-50 border border-information-200 rounded-lg p-4">
            <h4 className="text-theme font-semibold mb-2 flex items-center gap-2">
              <span>📝</span> About Leave Types
            </h4>
            <p className="text-sm text-theme mb-2">
              Create and manage different types of leave (CL, SL, EL, etc.) with
              specific rules for each type. Configure accrual, carry-forward,
              encashment, and document requirements.
            </p>
            <div className="bg-white rounded p-3 mt-2">
              <p className="text-xs font-medium text-theme mb-1">
                Example Scenario:
              </p>
              <p className="text-xs text-textLight">
                Create "Casual Leave (CL)" with: Code: CL, Accrual: Monthly 1.5
                days, Max Carry-Forward: 5 days, Encashable: Yes, Requires Doc
                Above: 3 days. Result: Employees get 1.5 CL days every month
                (18/year), can carry max 5 unused days to next year, can encash
                excess, and need medical certificate for leaves above 3 days.
              </p>
            </div>
          </div>
          <PageTableHeader
            isSearch
            onSearchChange={handleSearchChange}
            add
            onClickAdd={() => openModal("", actions_type.ADD_EDIT)}
            shorting
            sortingModule="leave"
            onSortingChange={sortingUIProps?.onSortingChange}
            sortingProps={sortingUIProps}
          />
          <div className="bg-white rounded-lg shadow-sm">
            <CommonTable
              search={search}
              TableHeaderData={TableHeaderData}
              tabledata={tableData.data}
              currentPage={currentPage}
              totalData={totalData}
              setCurrentPage={handlePageChange}
              loading={loading}
              action={tableData.action}
            />
          </div>
        </div>
      ),
    },
    {
      id: "policies",
      label: "Policies & Rules",
      content: (
        <PoliciesRulesSection
          fields={policiesFields}
          onSubmit={handlePoliciesSubmit}
          blackoutDates={blackoutDates}
          setBlackoutDates={setBlackoutDates}
          initialData={policiesInitialData}
        />
      ),
    },
    // {
    //   id: "org-settings",
    //   label: "Org Settings",
    //   content: (
    //     <OrgSettingsSection
    //       fields={orgSettingsFields}
    //       onSubmit={handleOrgSettingsSubmit}
    //     />
    //   ),
    // },
    {
      id: "approver-config",
      label: "Approver Config",
      content: (
        <ApproverConfigSection
          designations={designations}
          onSubmit={handleApproverConfigSubmit}
          initialData={leaveApprovalsInitialData}
        />
      ),
    },
    // {
    //   id: "balance-management",
    //   label: "Balance Management",
    //   content: (
    //     <BalanceManagementSection
    //       fields={balanceManagementFields}
    //       onAdjust={handleBalanceAdjust}
    //       onCSVImport={handleCSVImport}
    //       onEncashment={handleEncashment}
    //       onYearEndCarryForward={handleYearEndCarryForward}
    //     />
    //   ),
    // },
    // {
    //   id: "notifications",
    //   label: "Notifications",
    //   content: (
    //     <NotificationsSection
    //       fields={notificationsFields}
    //       onSubmit={handleNotificationsSubmit}
    //     />
    //   ),
    // },
    {
      id: "leave-allocation",
      label: "Leave Allocation",
      content: (
        <LeaveAllocationSection
          leaveTypes={
            tableData?.data?.map((item) => ({
              id: item.leave_type_id?.data || item.id?.data, // Numeric ID for API
              label: item.name?.data || item.code?.data || "Unknown",
              value: item.leave_type_id?.data || item.id?.data || "", // Same as ID for backwards compatibility
              code: item.code?.data || "", // Leave type code (CL, SL, etc.)
              gender_specific: item.gender_specific?.data || "all",
            })) || []
          }
          onIndividualAllocation={handleIndividualAllocation}
          onBulkAllocation={handleBulkAllocation}
          onAnnualAllocation={handleAnnualAllocation}
        />
      ),
    },
    // {
    //   id: "audit-logs",
    //   label: "Audit & Logs",
    //   content: <AuditLogsSection headers={auditLogsHeaders} />,
    // },
  ];

  // Fetch policies data on component mount
  useEffect(() => {
    fetchPoliciesDetail();
    fetchLeaveApprovals();
  }, []);

  return (
    <div className={"flex flex-col gap-4"}>
      <PageTableHeader breadcrumbs={items} />

      <div className="bg-white rounded-lg p-4">
        <CustomTab
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          className="!bg-light"
        />
        <div className="mt-4">
          <TabContainer tabs={tabs} activeTab={activeTab} />
        </div>
      </div>

      {action === actions_type.ADD_EDIT && (
        <BottomDrawer
          title={selectedId ? "Edit Leave-type" : "Add Leave-type"}
          close={handleCancel}
          isOpen={action === actions_type.ADD_EDIT}
          setIsOpen={handleCancel}
        >
          <CommonForm
            key={isEditValue?.id || "new"} // Force re-render when editing different items
            fields={comprehensiveFields}
            initialData={isEditValue ? isEditValue : {}}
            onSubmit={handleSubmit}
            submitButtonText="Submit"
            onCancel={handleCancel}
            cancelButtonText="Cancel"
            columnsPerRow={1}
            gap="gap-4"
            rowClassName=""
            fieldClassName="bg-white p-3 rounded shadow-sm"
          />
        </BottomDrawer>
      )}
    </div>
  );
};
export default ManageLeaveSetup;
